import React, {useState, useMemo, useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import compose from 'lodash/flowRight'
import {Alert, Button, Input} from 'reactstrap'

import useSmartNavigate from 'hooks/useSmartNavigate'

import {breadcrumbsStore} from 'appBase/TopNav'

import withUriParams from 'hocs/withUriParams'
import withGeneratorDataSource from 'hocs/withGeneratorDataSource'
import PanelModal from 'components/modals/PanelModal'
import SaveButtonsBlock from 'components/blocks/SaveButtonsBlock'
import GeneratorBlock from 'components/fieldBlocks/GeneratorBlock'
import NamedBlock from 'components/blocks/NamedBlock'
import Tree from 'components/blocks/Tree'
import ListWithSort from 'components/blocks/ListWithSort'

import {queryServiceUsers} from '../data/serviceQueries'
import {createUser, updateUser, deleteUser} from '../data/serviceMutations'
import {deleteStudentGroupAssignment} from 'appAdmin/data/adminMutations'

import entityUtils from 'utils/entityUtils'
import {parseBackendMessage} from 'utils/translationUtils'
import serviceStaticPrototypes from '../data/serviceStaticPrototypes.json'
import {SERVICE_USERS_VIEW} from 'constants/constants'


const UsersView = compose(
     withUriParams([['search', String, {optional: true}], ['userId', Number]]),
     queryServiceUsers,
     createUser,
     updateUser,
     deleteUser,
     deleteStudentGroupAssignment,
     withGeneratorDataSource
)((props) => {
    const {createUser, updateUser, deleteUser, deleteStudentGroupAssignment, user,
           dataSource, setDataSource, saveAndReset, isChanged, hasErrors, onChange, onValidate} = props;

    const [search, setSearch] = useState(props.search || "");
    const [userId, setUserId] = useState(props.userId);
    const [error, setError] = useState(null);

    const navigate = useSmartNavigate();
    const {i18n, t} = useTranslation();

    // preparing links
    breadcrumbsStore.register(SERVICE_USERS_VIEW);
    useEffect(() => {
        breadcrumbsStore.set(SERVICE_USERS_VIEW, {name: t('appService.users'), to: `${props.uri}`}, true);
    });

    useEffect(() =>
        setUserId(props.userId),
        [props.userId]);

    const users = props.serviceUsers && props.serviceUsers.map(u => ({
        name: u.name,
        fields: [
            u.id,
            u.name,
            u.email,
            u.talentId,
            u.active ? t('appService.views.UsersView.yes'): t('appService.views.UsersView.no')],
        actions: {
            "click": () => navigate(`${props.uri}/${search}/${u.id}${props.location.search}`),
            "removeConfirmation":  () =>
                deleteUser(u.id)
                    .then(({data: {deleteUser: {id, error}}}) => {
                        setError(error ? parseBackendMessage(error, t): null);
                    })
        }
    }));


    // user stable object for dataSource
    const userSource = useMemo(() => {
        if ([null, undefined].includes(userId)) {
            return null;
        }
        if (userId === 0) {
            return {};
        }
        return props.serviceUsers && props.serviceUsers.find(u => u.id === userId);
    }, [userId, props.serviceUsers]);


    // invalid userId, user not found
    if (userId && !userSource) {
        navigate(`${props.uri}/${search}${props.location.search}`);
    }


    // setting dataSource
    useEffect(() => {
        if (![undefined, null].includes(userId)) {
            setDataSource(
                userSource,
                (data) => {
                    data = entityUtils.filterFields(data, [
                        'id',
                        'name',
                        'email',
                        'talentId',
                        'password',
                        'active'
                    ]);

                    const mutationName = userId > 0 ? "updateUser": "createUser";
                    const mutationFunc = userId > 0 ? updateUser: createUser;
                    mutationFunc(data)
                        .then(({data: {[mutationName]: {id, error}}}) => {
                            setError(error ? parseBackendMessage(error, t): null);
                            if (!error) {
                                setUserId(null);
                                navigate(`${props.uri}/${search}${props.location.search}`);
                            }
                        });
                });
        }
    }, [createUser, updateUser, userId, userSource, setDataSource, t, navigate, props.uri, search, props.location.search]);

    const onCancel = () => {
        setDataSource(null);
        setUserId(null);
        navigate(`${props.uri}/${search}${props.location.search}`);
    };

    const nodes = [];
    userId && userSource && userSource.licenses.forEach(license => {
        const licenseNode = {
            id: 'l' + license.id,
            parent_id: null,
            name: license.name,
            items: []
        };
        nodes.push(licenseNode);

        license.events.forEach(event => {
            const eventNode = {
                id: 'e' + event.id,
                parent_id: 'l' + license.id,
                name: event.isArchived ?
                        t('appService.views.UsersView.archive') + event.name:
                        event.name,
                actions: {
                    removeConfirmation: () => deleteStudentGroupAssignment(event.studentGroupAssignment.id)
                }
            }
            licenseNode.items.push(eventNode);
        });
    });

    const proto = (userId === 0) ?
        serviceStaticPrototypes.createUserProto:
        serviceStaticPrototypes.updateUserProto;
    const title = (userId === 0) ?
        t('appService.views.UsersView.PanelModal.create'):
        t('appService.views.UsersView.PanelModal.update');

    return (
        <div className="d-flex flex-column h-100 w-100">
            <div className="flex-grow d-flex flex-column scroll-parent">

                {error &&
                <Alert className="mb-0" color="danger">{error}</Alert>}

                <div className="flex-grow d-flex scroll-parent">
                    <div className="flex-grow scroll">
                        <h5 className="text-center mt-3">{t('appService.users')}</h5>
                        <div className="container">
                            <Button color="primary" className="m-3"
                                    onClick={() => setUserId(0)}>
                                    {t('appService.views.UsersView.create')}
                            </Button>

                            <NamedBlock name={t('appService.views.UsersView.userSearch')}>
                                <Input type="text" value={search} className="my-3"
                                       placeholder={t('appService.views.UsersView.placeholder')}
                                       onChange={(e) => setSearch(e.nativeEvent.target.value)} />

                                <Button color="primary"
                                        onClick={() => navigate(`${props.uri}/${search}${props.location.search}`)}>
                                    {t('appService.views.UsersView.find')}
                                </Button>
                            </NamedBlock>

                            {users &&
                            <NamedBlock name={t('appService.views.UsersView.results') + " (" + users.length + ")"}>
                                <ListWithSort headers={[
                                    t('appService.views.UsersView.id'),
                                    t('appService.views.UsersView.name'),
                                    t('appService.views.UsersView.email'),
                                    t('appService.views.UsersView.talentId'),
                                    t('appService.views.UsersView.active')]}
                                    sizes={["50*", "100*", "200*", "50*", "50*"]} rows={users} />
                            </NamedBlock>}

                        </div>
                    </div>
                </div>


                {userSource &&
                <PanelModal show title={title} onClose={onCancel} noPadding scrollable>

                    <SaveButtonsBlock isChanged={isChanged} hasErrors={hasErrors} onSave={saveAndReset} onCancel={onCancel} />

                    <div className="scroll flex-grow d-flex flex-column workpanel-gray">
                        <GeneratorBlock data={dataSource} items={proto} onChange={onChange} onValidate={onValidate} wide  />

                        {!!userId && <>

                            {!!nodes.length && <>
                                <h5 className="text-center mt-3">
                                    {t('appService.views.UsersView.licenses')}
                                </h5>
                                <Tree nodes={nodes} />
                             </>}

                            {!nodes.length &&
                                <h5 className="text-center mt-3">
                                    {t('appService.views.UsersView.licensesNotFound')}
                                </h5>}

                           <div>
                               <Button color="primary" className="m-2"
                                       onClick={() => navigate(`/${i18n.resolvedLanguage}/${user.regionCode}/service/logs/(${userId})`)}>
                                   {t('appService.views.UsersView.allUserActions')}
                                </Button>
                            </div>
                        </>}

                    </div>
                </PanelModal>}
            </div>
        </div>);
});

export default UsersView;